import type { NavigationHookAfter } from 'vue-router'

import { useSession } from '@/features/auth'
import type { AppEnv } from '@/shared/config'
import { useEnv } from '@/shared/config'
import { pushToDataLayer } from '@/shared/lib/analytics/gtm'

// Route-level analytics side-effects (contract 05 §8/§11, ORP-18612).
// - `hook`: an `afterEach` that emits a GTM `page_change` on navigation.
//   Ported from v3 `router/index.js:263–275`, which fired only in prod AND only with a token.
//   Both gates now hold: the prod half via `env`, the token half via the module-06 session seam
//   (ORP-20389). The user/account/workspace ENRICHMENT of the payload is still deferred to
//   module 07 — until then the event degrades cleanly, but it no longer fires for a visitor
//   who has no session at all.
// - `trackFeature`: emits a GTM `feature_used` for a nav interaction
//   (ported from `SideNavItems.vue:372–384`). Legacy also gated on a user id + attached
//   user/workspace context; that enrichment is deferred with the account feature (07).
//
// Both are gated at THIS call site (analysis §7: the util stays decoupled): no-op unless the app
// runs in production with a configured `GTM_ID` and an authenticated visitor, so dev/test never
// touch `dataLayer` and an anonymous session emits nothing.

export interface RouteAnalytics {
  /** `router.afterEach` hook: emits a `page_change` GTM event (env- and token-gated). */
  hook: NavigationHookAfter
  /** Emit a `feature_used` GTM event for a nav interaction (env- and token-gated). */
  trackFeature: (featureName: string, featureCategory?: string) => void
}

/**
 * True only in production, with a configured GTM container, AND for a visitor who holds a token.
 *
 * The token half is v3's, verbatim: its `afterEach` returned early on `!authStore.token` before it
 * called `trackPageChange` (`router/index.js:265-266`). Without it, every logged-out visit to
 * login/signup emitted `page_change` and polluted the container (ORP-20389).
 */
function analyticsEnabled(env: AppEnv, token: string | null): boolean {
  return (
    env.MODE === 'production' && env.GTM_ID !== undefined && env.GTM_ID !== '' && Boolean(token)
  )
}

export function useRouteAnalytics(): RouteAnalytics {
  // Both reads stay INSIDE the closures: `useRouteAnalytics()` is called while the router is being
  // built (`createAppRouter.ts:40`), so resolving the store or the env here would bind them before
  // the app is composed — and the token has to be re-read on every navigation regardless.
  const hook: NavigationHookAfter = () => {
    const env = useEnv()
    if (!analyticsEnabled(env, useSession().token.value)) return
    pushToDataLayer({
      event: 'page_change',
      created_at: new Date().toISOString(),
      event_type: 'Core',
      front_url: env.API_APP_FRONT_URL ?? null,
    })
  }

  const trackFeature = (featureName: string, featureCategory = 'main'): void => {
    const env = useEnv()
    if (!analyticsEnabled(env, useSession().token.value)) return
    pushToDataLayer({
      event: 'feature_used',
      feature_name: featureName,
      feature_category: featureCategory,
      event_type: 'Optional',
      front_url: env.API_APP_FRONT_URL ?? null,
    })
  }

  return { hook, trackFeature }
}
